/**
 * "Usage & metering" — Billing section (ADR-0074).
 * Reconciled from the usage-metering service + per-tier UC-cap evaluation
 * (usage credits consumed per UTC calendar month; 80% "approaching"; a default
 * hard-stop at the included-UC cap, with opt-in pay-as-you-go overage beyond it)
 * and the usage dashboard component. Per-tier allowance numbers live on
 * /pricing — this page describes the mechanism.
 *
 * Under ADR-0442, repriced by ADR-0776, the mechanism is a POSTED PRICE per
 * interaction (6 / 15 / 18 / 30 UC, set by what the interaction did, read out of
 * a four-cell table), so the worked example publishes both
 * ends of an exact range rather than one figure "at a typical weight".
 */
import {
  EXAMPLE_CONVERSATIONS_PER_MONTH,
  EXAMPLE_FULL_USAGE_CREDITS_PER_MONTH,
  EXAMPLE_INTERACTIONS_PER_MONTH,
  EXAMPLE_SIMPLE_USAGE_CREDITS_PER_MONTH,
  TURNS_PER_CONVERSATION,
  // Direct module path, not the feature barrel: the barrel also exports the
  // `UcCalculator` component, and a docs CONTENT module must not drag React into
  // the docs chunk (the marketing entry budget is test-enforced).
} from '@/features/uc-calculator/usage-example';
import { formatLocaleNumber } from '@/i18n/format';

import type { DocPageContent } from '../types';

export const BILLING_USAGE_PAGE: DocPageContent = {
  slug: 'billing/usage',
  titleKey: 'docs.billing.usage.title',
  descriptionKey: 'docs.billing.usage.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.billing.usage.lead' },

    {
      kind: 'heading',
      level: 2,
      id: 'usage-credits',
      textKey: 'docs.billing.usage.usageCredits.heading',
    },
    { kind: 'paragraph', textKey: 'docs.billing.usage.usageCredits.body' },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.billing.usage.usageCredits.note.title',
      textKey: 'docs.billing.usage.usageCredits.note.body',
      links: {
        identity: {
          target: { docSlug: 'identity' },
          labelKey: 'docs.billing.usage.usageCredits.note.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'counting', textKey: 'docs.billing.usage.counting.heading' },
    { kind: 'paragraph', textKey: 'docs.billing.usage.counting.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.billing.usage.counting.item.once',
        'docs.billing.usage.counting.item.reset',
        'docs.billing.usage.counting.item.team',
      ],
    },

    { kind: 'heading', level: 2, id: 'examples', textKey: 'docs.billing.usage.examples.heading' },
    { kind: 'paragraph', textKey: 'docs.billing.usage.examples.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.billing.usage.examples.light.term',
          descKey: 'docs.billing.usage.examples.light.desc',
        },
        {
          termKey: 'docs.billing.usage.examples.medium.term',
          descKey: 'docs.billing.usage.examples.medium.desc',
        },
        {
          termKey: 'docs.billing.usage.examples.heavy.term',
          descKey: 'docs.billing.usage.examples.heavy.desc',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.billing.usage.examples.estimate.title',
      textKey: 'docs.billing.usage.examples.estimate.body',
      // Figures are DERIVED (`features/uc-calculator/usage-example.ts`), never
      // authored into the six catalogs — see `DocTextParams`. Locale-formatted
      // for each locale's own digit grouping.
      params: (locale) => ({
        conversations: formatLocaleNumber(EXAMPLE_CONVERSATIONS_PER_MONTH, locale),
        turns: formatLocaleNumber(TURNS_PER_CONVERSATION, locale),
        interactions: formatLocaleNumber(EXAMPLE_INTERACTIONS_PER_MONTH, locale),
        ucSimple: formatLocaleNumber(EXAMPLE_SIMPLE_USAGE_CREDITS_PER_MONTH, locale),
        ucFull: formatLocaleNumber(EXAMPLE_FULL_USAGE_CREDITS_PER_MONTH, locale),
      }),
      links: {
        pricing: { target: { path: '/pricing' }, labelKey: 'docs.common.link.pricing' },
      },
    },

    { kind: 'heading', level: 2, id: 'allowance', textKey: 'docs.billing.usage.allowance.heading' },
    {
      kind: 'paragraph',
      textKey: 'docs.billing.usage.allowance.body',
      links: {
        pricing: { target: { path: '/pricing' }, labelKey: 'docs.common.link.pricing' },
      },
    },

    {
      kind: 'heading',
      level: 2,
      id: 'thresholds',
      textKey: 'docs.billing.usage.thresholds.heading',
    },
    { kind: 'paragraph', textKey: 'docs.billing.usage.thresholds.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.billing.usage.status.within.term',
          descKey: 'docs.billing.usage.status.within.desc',
        },
        {
          termKey: 'docs.billing.usage.status.approaching.term',
          descKey: 'docs.billing.usage.status.approaching.desc',
        },
        {
          termKey: 'docs.billing.usage.status.reached.term',
          descKey: 'docs.billing.usage.status.reached.desc',
        },
      ],
    },

    { kind: 'heading', level: 2, id: 'reached', textKey: 'docs.billing.usage.reached.heading' },
    { kind: 'paragraph', textKey: 'docs.billing.usage.reached.body' },
    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.billing.usage.reached.tip.title',
      textKey: 'docs.billing.usage.reached.tip.body',
      links: {
        upgrade: {
          target: { docSlug: 'billing/subscription' },
          labelKey: 'docs.billing.usage.reached.tip.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'tracking', textKey: 'docs.billing.usage.tracking.heading' },
    { kind: 'paragraph', textKey: 'docs.billing.usage.tracking.body' },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.billing.usage.next.plans.title',
          textKey: 'docs.billing.usage.next.plans.text',
          to: { docSlug: 'billing/plans' },
        },
        {
          titleKey: 'docs.billing.usage.next.limits.title',
          textKey: 'docs.billing.usage.next.limits.text',
          to: { docSlug: 'billing/limits' },
        },
      ],
    },
  ],
};
